/**
 * metrics-engagement-adapter.ts — 数据中台内容消费 L2-B「真 API DTO → 页面消费形状」塑形
 *
 * GET /api/v1/metrics/engagement 返回 snake_case DTO（服务端 DTO interface = packages/server
 * engagement-metrics-read-service.ts 的 EngagementData；admin 自有 API，随 P1-4 dev-docs 收录）。
 * 混合就绪度（禁假数据）：
 * - read_trend（READY）：reading_sessions 真实源，逐业务日人均阅读章节 / 分钟 live 透传；
 * - retention_curves（留存值 PENDING · 骨架 live）：block status=PENDING_INSTRUMENTATION →
 *   pending 标记（页面整块 PendingBlock 灰显，cohort 周数 / 规模合计在说明中带出）；
 *   服务端 as-built 恒 PENDING，DTO 侧按 EngagementBlockStatus 宽联合镜像——behavior sink
 *   点亮后 status 翻 READY，页面自动切多 cohort 曲线（禁写死 PENDING 分支）；
 * - content_types（PARTIAL）：readers / unlock_rate live；finish_rate / avg_chapters 口径待定
 *   → null（页面按格灰显 —，禁臆造）。
 * 契约缺字段落 null / pending，不编造数据。
 */

import { PENDING_INSTRUMENTATION } from '@novel-admin/shared';

// ---------- DTO（snake_case，镜像服务端 EngagementData） ----------

/** 块级就绪度（镜像服务端 EngagementBlockStatus；retention as-built 恒 PENDING、就绪后翻 READY） */
export type EngagementBlockStatusDto = 'READY' | 'PARTIAL' | typeof PENDING_INSTRUMENTATION;

/** 留存里程碑 key 闭集（cohort_metrics_daily 仅 d1/d7/d30 有列，d3/d14 无源 → 恒 null） */
export type RetentionMilestoneKey = 'd1' | 'd3' | 'd7' | 'd14' | 'd30';

export interface EngagementReadTrendPointDto {
  /** 业务日 YYYY-MM-DD（reading_sessions.date_key，day_cut_timezone 口径） */
  date: string;
  /** 人均阅读章节数（1 位小数） */
  avg_chapters: number;
  /** 人均阅读分钟（1 位小数） */
  avg_minutes: number;
}

export interface EngagementRetentionCohortDto {
  /** cohort 周起点（周一 UTC）YYYY-MM-DD */
  cohort_week: string;
  /** cohort 规模（Σcohort_size）——live 骨架 */
  cohort_size: number;
  /** 各里程碑留存率（percent）——as-built 全 null（behavior 未流入，禁假留存率） */
  retention_pct: Record<RetentionMilestoneKey, number | null>;
}

export interface EngagementContentTypeRowDto {
  category_id: string;
  /** 分类名多语言池（zh→en→第一个取展示名，对齐 dashboard top_books.category） */
  name_i18n: Record<string, string>;
  /** 分类内 distinct 阅读用户（窗内 reading_progress）——live */
  readers: number;
  /** 解锁转化率（percent）；readers=0 → null——live */
  unlock_rate: number | null;
  /** 完读率——口径待定 → 恒 null（服务端禁臆造） */
  finish_rate: number | null;
  /** 人均章节——口径待定 → 恒 null */
  avg_chapters: number | null;
}

export interface MetricsEngagementResponse {
  app_scope: string;
  range: string;
  scope: 'all' | 'app';
  window: { from: string; to: string };
  computed_at: string;
  data_freshness: { txn: string; behavioral: string };
  read_trend: { status: 'READY'; points: EngagementReadTrendPointDto[] };
  retention_curves: {
    status: EngagementBlockStatusDto;
    /** 里程碑天数 [1,3,7,14,30] */
    milestones: number[];
    cohorts: EngagementRetentionCohortDto[];
  };
  content_types: { status: EngagementBlockStatusDto; rows: EngagementContentTypeRowDto[] };
}

// ---------- 视图模型（camelCase，页面消费） ----------

export interface EngagementRetentionCohortView {
  cohortWeek: string;
  cohortSize: number;
  /** 与 milestones 同序对齐的留存率数组（LineChartX values；null 断线不补零） */
  retentionPct: Array<number | null>;
}

export interface EngagementContentTypeRowView {
  categoryId: string;
  /** 展示名（name_i18n zh→en→第一个） */
  name: string;
  /** live：窗内阅读用户 */
  readers: number;
  /** live：解锁转化率 %（1 位小数）；readers=0 → null */
  unlockRate: number | null;
  /** 口径待定 → null（页面灰显 —） */
  finishRate: number | null;
  /** 口径待定 → null */
  avgChapters: number | null;
}

export interface MetricsEngagementViewData {
  computedAt: string | null;
  window: { from: string; to: string };
  /** READY：逐业务日人均阅读深度（live，空数据 → [] 真实空态而非 PENDING） */
  readTrend: { points: Array<{ date: string; avgChapters: number; avgMinutes: number }> };
  retention: {
    /** 留存值未就绪 → 整块 PendingBlock 灰显（骨架/规模仍在说明中带出） */
    pending: boolean;
    /** 里程碑天数 [1,3,7,14,30]（曲线 X 轴 D1…D30） */
    milestones: number[];
    cohorts: EngagementRetentionCohortView[];
    /** 窗内周 cohort 规模合计（live 骨架，PENDING 说明带出） */
    totalCohortSize: number;
  };
  contentTypes: {
    /** 整块未就绪（as-built 恒 false：readers/unlock live → PARTIAL） */
    pending: boolean;
    /** 部分列未就绪（finish_rate / avg_chapters 口径待定 → 按格灰显） */
    partial: boolean;
    rows: EngagementContentTypeRowView[];
  };
}

/** name_i18n 取展示名：优先 zh → en → 第一个 value（对齐 dashboard-adapter pickI18nName） */
function pickI18nName(nameI18n: Record<string, string>): string {
  return nameI18n.zh || nameI18n.en || Object.values(nameI18n)[0] || '';
}

/** milestone 天数 → retention_pct 对象 key（1 → d1）；越界防御 → null */
function retentionAt(
  pct: Record<RetentionMilestoneKey, number | null>,
  day: number,
): number | null {
  const key = `d${day}` as RetentionMilestoneKey;
  return key in pct ? pct[key] : null;
}

export function adaptMetricsEngagement(dto: MetricsEngagementResponse): MetricsEngagementViewData {
  const retentionPending = dto.retention_curves.status === PENDING_INSTRUMENTATION;
  const milestones = dto.retention_curves.milestones;

  return {
    computedAt: dto.computed_at ?? null,
    window: dto.window,
    readTrend: {
      points: dto.read_trend.points.map((p) => ({
        date: p.date,
        avgChapters: p.avg_chapters,
        avgMinutes: p.avg_minutes,
      })),
    },
    retention: {
      pending: retentionPending,
      milestones,
      cohorts: dto.retention_curves.cohorts.map((c) => ({
        cohortWeek: c.cohort_week,
        cohortSize: c.cohort_size,
        retentionPct: milestones.map((d) => retentionAt(c.retention_pct, d)),
      })),
      totalCohortSize: dto.retention_curves.cohorts.reduce((s, c) => s + c.cohort_size, 0),
    },
    contentTypes: {
      pending: dto.content_types.status === PENDING_INSTRUMENTATION,
      partial: dto.content_types.status === 'PARTIAL',
      rows: dto.content_types.rows.map((r) => ({
        categoryId: r.category_id,
        name: pickI18nName(r.name_i18n),
        readers: r.readers,
        unlockRate: r.unlock_rate,
        finishRate: r.finish_rate,
        avgChapters: r.avg_chapters,
      })),
    },
  };
}
